import { useMutation, useQuery } from "@tanstack/react-query";

import { useAxios, useApiResponseParser } from "../";

export interface ProjectDetails {
    Id?: string;
    Name: string;
    Description?: string;
    ProjectType?: string;
    EagleViewOrderId?: string;
    SalesmanName?: string;
    SecondarySalesmanName?: string;
    PrimarySalesmanId?: string;
    StartDate: string; // ISO string (e.g., from JSON), convert to Date when needed
    EstimatedEndDate?: string;
    ActualEndDate?: string;
    CustomerName?: string;
    CustomerPhoneNumber?: string;
    CustomerStreet?: string;
    CustomerCity?: string;
    CustomerState?: string;
    CustomerZipCode?: string;
    CustomerCountry?: string;
    InsuranceProvider?: string;
    InsuranceClaimNumber?: string;
    InsurancePolicyNumber?: string;
    InsuranceAgentNumber?: string;
    InsuranceAgentEmail?: string;
    Lead?: {
        Id: string;
        LeadSourcesId: string;
        Name?: string | null;
        LeadSource?: {
            Id: string;
            Name: string;
        } | null;
    } | null;
}

export default function useCreateProject() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();

    function useCreateProject() {
        return useMutation({
            mutationFn: (parameters: ProjectDetails) =>
                axiosPrivate.post(`/v1/project/create`, parameters).then(parseSuccessResponseData),
        });
    }

    function useCreateQuickProject() {
        return useMutation({
            mutationFn: (parameters: Partial<ProjectDetails>) =>
                axiosPrivate.post(`/v1/project/create-quick`, parameters).then(parseSuccessResponseData),
        });
    }

    function useProjectAttributeUpdate() {
        return useMutation({
            mutationFn: (parameters: any) =>
                axiosPrivate.put(`/v1/project/update`, parameters).then(parseSuccessResponseData),
        });
    }

    function useProjectUpdate() {
        return useMutation({
            mutationFn: (parameters: { Id: string } & any) =>
                axiosPrivate.post(`/v1/project/update`, parameters).then(parseSuccessResponseData),
        });
    }

    function useRevertProject() {
        return useMutation({
            mutationFn: (id: string) => axiosPrivate.delete(`/v1/project/revert/${id}`).then(parseSuccessResponseData),
        });
    }

    function useList() {
        return useMutation({
            mutationFn: () => axiosPrivate.get(`/v1/project/list`).then(parseSuccessResponseData),
        });
    }

    function useListById() {
        return useMutation({
            mutationFn: (params: { Id: string }) =>
                axiosPrivate.get(`/v1/project/details`, { params }).then(parseSuccessResponseData),
        });
    }

    function useGetProjectById(Id: string) {
        return useQuery({
            queryKey: ["project", Id],
            queryFn: () => axiosPrivate.get(`/v1/project/details`, { params: { Id } }).then(parseSuccessResponseData),
            enabled: !!Id,
        });
    }

    function useGetEagleViewReport() {
        return useMutation({
            mutationFn: (params: { ReportId: string }) =>
                axiosPrivate.get(`/v1/eagleview/load-report`, { params }).then(parseSuccessResponseData),
        });
    }

    function useGetProjectStatusSummary() {
        return useQuery({
            queryKey: ["project-summary"],
            queryFn: async () => {
                const response = await axiosPrivate.get(`/v1/project/status`);
                return parseSuccessResponseData(response);
            },
        });
    }

    function useGetEagleViewOrders() {
        return useMutation({
            mutationFn: () => axiosPrivate.get(`/v1/eagleview/orders`).then(parseSuccessResponseData),
        });
    }

    return {
        useCreateProject,
        useCreateQuickProject,
        useList,
        useListById,
        useGetProjectById,
        useGetEagleViewReport,
        useGetEagleViewOrders,
        useGetProjectStatusSummary,
        useProjectAttributeUpdate,
        useProjectUpdate,
        useRevertProject,
    };
}
